class LookiMediaReviews extends HTMLElement {
  connectedCallback() {
    this.abort = new AbortController();
    this.frame = requestAnimationFrame(() => this.init());
  }
  init() {
    this.logos = [...this.querySelectorAll('[data-review-logo]')];
    this.panels = [...this.querySelectorAll('[data-review-panel]')];
    if (!this.logos.length) return;
    this.motion = matchMedia('(prefers-reduced-motion: reduce)');
    this.paused = this.motion.matches;
    this.index = 0;
    this.visible = false;
    const on = (target, type, callback, options = {}) => target.addEventListener(type, callback, { ...options, signal: this.abort.signal });
    on(this, 'click', event => {
      if (this.swiped) { event.preventDefault(); return; }
      const logo = event.target.closest('[data-review-logo]');
      if (logo) this.select(Number(logo.dataset.reviewLogo));
      if (event.target.closest('[data-review-next]')) this.select(this.index + 1);
      if (event.target.closest('[data-review-prev]')) this.select(this.index - 1);
      if (event.target.closest('[data-review-play]')) { this.paused = !this.paused; this.updatePlay(); this.schedule(); }
    }, { capture: true });
    on(this, 'keydown', event => {
      if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
      event.preventDefault();
      if (event.key === 'Home') this.select(0);
      else if (event.key === 'End') this.select(this.logos.length - 1);
      else this.select(this.index + (event.key === 'ArrowLeft' ? -1 : 1));
    });
    on(this, 'mouseenter', () => { this.hovered = true; this.schedule(); });
    on(this, 'mouseleave', () => { this.hovered = false; this.schedule(); });
    on(this, 'focusin', () => { this.focused = true; this.schedule(); });
    on(this, 'focusout', event => { this.focused = this.contains(event.relatedTarget); this.schedule(); });
    on(document, 'visibilitychange', () => this.schedule());
    on(this.motion, 'change', () => { if (this.motion.matches) this.paused = true; this.updatePlay(); this.schedule(); });
    const track = this.querySelector('[data-review-logos]');
    on(track, 'dragstart', event => event.preventDefault());
    on(track, 'pointerdown', event => {
      if (event.button !== 0) return;
      this.start = { x: event.clientX, y: event.clientY, id: event.pointerId };
      this.swiped = false;
      this.schedule();
    });
    on(window, 'pointerup', event => {
      if (!this.start || this.start.id !== event.pointerId) return;
      const dx = event.clientX - this.start.x;
      const dy = event.clientY - this.start.y;
      this.start = null;
      if (Math.abs(dx) > 32 && Math.abs(dx) > Math.abs(dy)) { this.swiped = true; this.select(this.index + (dx < 0 ? 1 : -1)); }
      this.schedule();
      clearTimeout(this.reset);
      this.reset = setTimeout(() => { this.swiped = false; }, 0);
    });
    on(window, 'pointercancel', () => { this.start = null; this.schedule(); });
    on(document, 'shopify:block:select', event => {
      const index = this.logos.findIndex(logo => logo.dataset.blockId === event.detail?.blockId);
      if (index < 0) return;
      this.editing = true;
      this.select(index);
    });
    on(document, 'shopify:block:deselect', () => { this.editing = false; this.schedule(); });
    this.observer = new IntersectionObserver(([entry]) => { this.visible = entry.isIntersecting; this.schedule(); }, { threshold: .15 });
    this.observer.observe(this);
    this.updatePlay();
    this.select(0);
  }
  select(index) {
    const total = this.logos.length;
    this.index = (index + total) % total;
    this.logos.forEach((logo, i) => {
      let position = (i - this.index + total) % total;
      if (position > Math.floor(total / 2)) position -= total;
      const previous = Number(logo.style.getPropertyValue('--vr-position'));
      logo.classList.toggle('is-wrapping', Math.abs(previous - position) > 1);
      logo.style.setProperty('--vr-position', position);
      const active = i === this.index;
      const visible = Math.abs(position) <= 1;
      logo.dataset.reviewSide = active ? 'center' : position < 0 ? 'left' : 'right';
      logo.classList.toggle('is-active', active);
      logo.setAttribute('aria-pressed', String(active));
      logo.setAttribute('aria-hidden', String(!visible));
      logo.tabIndex = visible ? 0 : -1;
      this.panels[i].classList.toggle('is-active', active);
      this.panels[i].setAttribute('aria-hidden', String(!active));
      this.panels[i].inert = !active;
    });
    this.schedule();
  }
  updatePlay() {
    const button = this.querySelector('[data-review-play]');
    if (!button) return;
    button.setAttribute('aria-label', this.paused ? button.dataset.playLabel : button.dataset.pauseLabel);
    button.firstElementChild.textContent = this.paused ? '▶' : 'Ⅱ';
  }
  schedule() {
    clearTimeout(this.timer);
    if (this.dataset.autoplay !== 'true' || this.logos.length < 2 || this.paused || !this.visible || this.hovered || this.focused || this.editing || this.start || document.hidden) return;
    this.timer = setTimeout(() => this.select(this.index + 1), Number(this.dataset.interval) || 5000);
  }
  disconnectedCallback() {
    this.abort?.abort();
    this.observer?.disconnect();
    clearTimeout(this.timer);
    clearTimeout(this.reset);
    cancelAnimationFrame(this.frame);
  }
}
if (!customElements.get('looki-media-reviews')) customElements.define('looki-media-reviews', LookiMediaReviews);
